import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  CircularProgress,
  Autocomplete,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Work as WorkIcon,
  AccountTree as AccountTreeIcon,
  School as SchoolIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PageWithHeader from '../common/PageWithHeader';
import {
  useGetMyJobDescriptionQuery,
  useCreateMyJobDescriptionMutation,
  useUpdateMyJobDescriptionMutation,
  useDeleteMyJobDescriptionMutation,
  useGetJobDescriptionDepartmentsQuery,
  useGetJobDescriptionGradesQuery,
  useGetJobDescriptionsActiveQuery,
} from '../../redux/jobDescriptionApi';
import type {
  JobDescription,
  Qualification,
  ProfessionalRegistration,
  AuthorityLimit,
  BSCPerspective,
} from './bscTypes';

const DEFAULT_BSC_PERSPECTIVES: BSCPerspective[] = [
  { name: 'Financial', kras: [] },
  { name: 'Customer', kras: [] },
  { name: 'Internal Processes', kras: [] },
  { name: 'Learning & Growth', kras: [] },
];

const QUALIFICATION_LEVELS = [
  'High School Diploma',
  'Certificate',
  'Diploma',
  'Bachelor\'s Degree',
  'Honours Degree',
  'Master\'s Degree',
  'Doctoral Degree',
  'Professional Certification',
];

const AUTHORITY_CATEGORIES = [
  'Financial',
  'Procurement',
  'HR/Personnel',
  'Operational',
  'Legal/Contracts',
  'IT/Technology',
];

const CURRENCIES = ['USD', 'ZWL', 'ZAR', 'EUR', 'GBP'];

const MyJobDescriptionForm: React.FC = () => {
  const navigate = useNavigate();
  
  // API Hooks
  const { data: existingJD, isLoading: loadingJD, refetch } = useGetMyJobDescriptionQuery();
  const { data: departments = [] } = useGetJobDescriptionDepartmentsQuery();
  const { data: grades = [] } = useGetJobDescriptionGradesQuery();
  const { data: allJobDescriptions = [] } = useGetJobDescriptionsActiveQuery();
  
  const [createJD, { isLoading: creating }] = useCreateMyJobDescriptionMutation();
  const [updateJD, { isLoading: updating }] = useUpdateMyJobDescriptionMutation();
  const [deleteJD, { isLoading: deleting }] = useDeleteMyJobDescriptionMutation();
  
  const isEdit = !!existingJD;
  const availableSupervisorJDs = allJobDescriptions.filter(jd => jd.id !== existingJD?.id);
  
  // Form State
  const [formData, setFormData] = useState<Partial<JobDescription>>({
    title: '',
    grade: '',
    department: '',
    jobPurpose: '',
    reportsTo: '',
    reportsToJobDescriptionId: '',
    limitsToAuthority: [],
    minimumQualifications: [],
    minimumJobKnowledge: [],
    minimumExperienceYears: '',
    minimumExperienceDescription: '',
    professionalRegistrations: [],
    otherRequirements: [],
    bscPerspectives: DEFAULT_BSC_PERSPECTIVES,
    requiredCompetencies: [],
    isActive: true,
  });
  
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [newKnowledge, setNewKnowledge] = useState('');
  const [newRequirement, setNewRequirement] = useState('');
  const [newCompetency, setNewCompetency] = useState('');
  const [kraInputs, setKraInputs] = useState<string[]>(['', '', '', '']);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Load existing data when editing
  useEffect(() => {
    if (existingJD) {
      setFormData({
        ...existingJD,
        bscPerspectives: existingJD.bscPerspectives?.length > 0 
          ? existingJD.bscPerspectives 
          : DEFAULT_BSC_PERSPECTIVES,
      });
    }
  }, [existingJD]);
  
  const handleChange = (field: keyof JobDescription, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  // Qualification handlers
  const addQualification = () => {
    const newQual: Qualification = {
      name: '',
      level: '',
      field: '',
      isRequired: true,
    };
    setFormData(prev => ({
      ...prev,
      minimumQualifications: [...(prev.minimumQualifications || []), newQual],
    }));
  };
  
  const updateQualification = (index: number, field: keyof Qualification, value: any) => {
    setFormData(prev => {
      const quals = [...(prev.minimumQualifications || [])];
      quals[index] = { ...quals[index], [field]: value };
      return { ...prev, minimumQualifications: quals };
    });
  };
  
  const removeQualification = (index: number) => {
    setFormData(prev => ({
      ...prev,
      minimumQualifications: prev.minimumQualifications?.filter((_, i) => i !== index),
    }));
  };
  
  // Professional Registration handlers
  const addRegistration = () => {
    const newReg: ProfessionalRegistration = {
      body: '',
      membershipType: '',
      isRequired: true,
    };
    setFormData(prev => ({
      ...prev,
      professionalRegistrations: [...(prev.professionalRegistrations || []), newReg],
    }));
  };
  
  const updateRegistration = (index: number, field: keyof ProfessionalRegistration, value: any) => {
    setFormData(prev => {
      const regs = [...(prev.professionalRegistrations || [])];
      regs[index] = { ...regs[index], [field]: value };
      return { ...prev, professionalRegistrations: regs };
    });
  };
  
  const removeRegistration = (index: number) => {
    setFormData(prev => ({
      ...prev,
      professionalRegistrations: prev.professionalRegistrations?.filter((_, i) => i !== index),
    }));
  };
  
  // Authority Limit handlers
  const addAuthorityLimit = () => {
    const newLimit: AuthorityLimit = {
      category: '',
      description: '',
      monetaryLimit: undefined,
      currency: 'USD',
      approvalRequired: '',
    };
    setFormData(prev => ({
      ...prev,
      limitsToAuthority: [...(prev.limitsToAuthority || []), newLimit],
    }));
  };
  
  const updateAuthorityLimit = (index: number, field: keyof AuthorityLimit, value: any) => {
    setFormData(prev => {
      const limits = [...(prev.limitsToAuthority || [])];
      limits[index] = { ...limits[index], [field]: value };
      return { ...prev, limitsToAuthority: limits };
    });
  };
  
  const removeAuthorityLimit = (index: number) => {
    setFormData(prev => ({
      ...prev,
      limitsToAuthority: prev.limitsToAuthority?.filter((_, i) => i !== index),
    }));
  };
  
  // BSC KRA handlers
  const addKRA = (perspectiveIndex: number, kraText: string) => {
    if (!kraText.trim()) return;
    setFormData(prev => {
      const perspectives = [...(prev.bscPerspectives || [])];
      perspectives[perspectiveIndex] = {
        ...perspectives[perspectiveIndex],
        kras: [...perspectives[perspectiveIndex].kras, kraText.trim()],
      };
      return { ...prev, bscPerspectives: perspectives };
    });
    // Clear input
    const newInputs = [...kraInputs];
    newInputs[perspectiveIndex] = '';
    setKraInputs(newInputs);
  };
  
  const removeKRA = (perspectiveIndex: number, kraIndex: number) => {
    setFormData(prev => {
      const perspectives = [...(prev.bscPerspectives || [])];
      perspectives[perspectiveIndex] = {
        ...perspectives[perspectiveIndex],
        kras: perspectives[perspectiveIndex].kras.filter((_, i) => i !== kraIndex),
      };
      return { ...prev, bscPerspectives: perspectives };
    });
  };
  
  // String array handlers
  const addToArray = (field: 'minimumJobKnowledge' | 'otherRequirements' | 'requiredCompetencies', value: string) => {
    if (!value.trim()) return;
    setFormData(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), value.trim()],
    }));
  };
  
  const removeFromArray = (field: 'minimumJobKnowledge' | 'otherRequirements' | 'requiredCompetencies', index: number) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field]?.filter((_, i) => i !== index),
    }));
  };
  
  const handleSubmit = async () => {
    try {
      setError(null);
      setSuccess(null);
      
      if (!formData.title?.trim()) {
        setError('Job Title is required');
        return;
      }
      
      const submitData = {
        ...formData,
        title: formData.title.trim(),
        isActive: formData.isActive ?? true,
        bscPerspectives: formData.bscPerspectives || DEFAULT_BSC_PERSPECTIVES,
      };
      
      // Remove fields that shouldn't be sent for self-managed JD
      delete submitData.assignedUserId;
      delete submitData.assignedUserName;
      delete submitData.createdByUserId;
      delete submitData.createdByName;
      delete submitData.id;
      delete submitData.createdAt;
      delete submitData.updatedAt;
      
      if (isEdit) {
        await updateJD(submitData).unwrap();
        setSuccess('Job Description updated successfully!');
        refetch();
      } else {
        await createJD(submitData as any).unwrap();
        setSuccess('Job Description created successfully!');
        setTimeout(() => navigate('/reviews/job-descriptions/my'), 1500);
      }
    } catch (err: any) {
      console.error('Submit error:', err);
      const errorMessage = err?.data?.error?.message || err?.message || 'Failed to save Job Description';
      setError(errorMessage);
    }
  };
  
  const handleDelete = async () => {
    try {
      await deleteJD().unwrap();
      setShowDeleteDialog(false);
      setSuccess('Job Description deleted successfully!');
      setTimeout(() => navigate('/reviews/bsc'), 1500);
    } catch (err: any) {
      console.error('Delete error:', err);
      const errorMessage = err?.data?.error?.message || err?.message || 'Failed to delete Job Description';
      setError(errorMessage);
      setShowDeleteDialog(false);
    }
  };
  
  if (loadingJD) {
    return (
      <PageWithHeader>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </PageWithHeader>
    );
  }
  
  return (
    <PageWithHeader>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <IconButton onClick={() => navigate('/reviews/job-descriptions/my')}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight="bold">
              {isEdit ? 'Edit My Job Description' : 'Create My Job Description'}
            </Typography>
          </Box>
          
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            {isEdit && (
              <Button
                variant="outlined"
                color="error"
                onClick={() => setShowDeleteDialog(true)}
                disabled={deleting}
              >
                Delete
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSubmit}
              disabled={creating || updating}
            >
              {creating || updating ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Paper>
        
        {/* Job Description Section */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={1}>
              <WorkIcon color="primary" />
              <Typography variant="h6">Job Description</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Job Title"
                  value={formData.title || ''}
                  onChange={(e) => handleChange('title', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  freeSolo
                  options={departments}
                  value={formData.department || ''}
                  onChange={(_, value) => handleChange('department', value || '')}
                  onInputChange={(_, value) => handleChange('department', value)}
                  renderInput={(params) => (
                    <TextField {...params} label="Department" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  freeSolo
                  options={grades}
                  value={formData.grade || ''}
                  onChange={(_, value) => handleChange('grade', value || '')}
                  onInputChange={(_, value) => handleChange('grade', value)}
                  renderInput={(params) => (
                    <TextField {...params} label="Grade" />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Job Purpose"
                  value={formData.jobPurpose || ''}
                  onChange={(e) => handleChange('jobPurpose', e.target.value)}
                  helperText="Brief description of the main purpose of this role"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Reports To (Job Description)</InputLabel>
                  <Select
                    value={formData.reportsToJobDescriptionId || ''}
                    onChange={(e) => {
                      const selectedJD = availableSupervisorJDs.find(jd => jd.id === e.target.value);
                      handleChange('reportsToJobDescriptionId', e.target.value);
                      handleChange('reportsTo', selectedJD?.title || '');
                    }}
                    label="Reports To (Job Description)"
                  >
                    <MenuItem value="">None</MenuItem>
                    {availableSupervisorJDs.map((jd) => (
                      <MenuItem key={jd.id} value={jd.id}>
                        {jd.title} {jd.department ? `(${jd.department})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
        
        {/* BSC Perspectives & Key Result Areas */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={1}>
              <AssessmentIcon color="primary" />
              <Typography variant="h6">BSC Perspectives & Key Result Areas</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {(formData.bscPerspectives || DEFAULT_BSC_PERSPECTIVES).map((perspective, pIdx) => (
                <Grid item xs={12} md={6} key={pIdx}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                        {perspective.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Add KRA..."
                          value={kraInputs[pIdx] || ''}
                          onChange={(e) => {
                            const newInputs = [...kraInputs];
                            newInputs[pIdx] = e.target.value;
                            setKraInputs(newInputs);
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              addKRA(pIdx, kraInputs[pIdx] || '');
                            }
                          }}
                        />
                        <IconButton
                          color="primary"
                          onClick={() => addKRA(pIdx, kraInputs[pIdx] || '')}
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {perspective.kras.map((kra, kraIdx) => (
                          <Chip
                            key={kraIdx}
                            label={kra}
                            onDelete={() => removeKRA(pIdx, kraIdx)}
                            size="small"
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
        
        {/* Inherent Job Requirements */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={1}>
              <SchoolIcon color="primary" />
              <Typography variant="h6">Inherent Job Requirements</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Minimum Qualifications
            </Typography>
            {(formData.minimumQualifications || []).map((qual, idx) => (
              <Paper key={idx} sx={{ p: 2, mb: 2 }} variant="outlined">
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Qualification Name"
                      value={qual.name}
                      onChange={(e) => updateQualification(idx, 'name', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Level</InputLabel>
                      <Select
                        value={qual.level}
                        onChange={(e) => updateQualification(idx, 'level', e.target.value)}
                        label="Level"
                      >
                        {QUALIFICATION_LEVELS.map((level) => (
                          <MenuItem key={level} value={level}>{level}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Field of Study"
                      value={qual.field || ''}
                      onChange={(e) => updateQualification(idx, 'field', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={1}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={qual.isRequired}
                          onChange={(e) => updateQualification(idx, 'isRequired', e.target.checked)}
                        />
                      }
                      label="Req"
                    />
                  </Grid>
                  <Grid item xs={12} md={1}>
                    <IconButton color="error" onClick={() => removeQualification(idx)}>
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            ))}
            <Button startIcon={<AddIcon />} onClick={addQualification} sx={{ mb: 3 }}>
              Add Qualification
            </Button>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Experience
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Minimum Years of Experience"
                  value={formData.minimumExperienceYears || ''}
                  onChange={(e) => handleChange('minimumExperienceYears', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Experience Description"
                  value={formData.minimumExperienceDescription || ''}
                  onChange={(e) => handleChange('minimumExperienceDescription', e.target.value)}
                />
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Minimum Job Knowledge
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Add knowledge requirement..."
                value={newKnowledge}
                onChange={(e) => setNewKnowledge(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addToArray('minimumJobKnowledge', newKnowledge);
                    setNewKnowledge('');
                  }
                }}
              />
              <Button
                variant="outlined"
                onClick={() => {
                  addToArray('minimumJobKnowledge', newKnowledge);
                  setNewKnowledge('');
                }}
              >
                Add
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {(formData.minimumJobKnowledge || []).map((item, idx) => (
                <Chip
                  key={idx}
                  label={item}
                  onDelete={() => removeFromArray('minimumJobKnowledge', idx)}
                />
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
        
        {/* Limits to Authority */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={1}>
              <AccountTreeIcon color="primary" />
              <Typography variant="h6">Limits to Authority</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {(formData.limitsToAuthority || []).map((limit, idx) => (
              <Paper key={idx} sx={{ p: 2, mb: 2 }} variant="outlined">
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={limit.category}
                        onChange={(e) => updateAuthorityLimit(idx, 'category', e.target.value)}
                        label="Category"
                      >
                        {AUTHORITY_CATEGORIES.map((cat) => (
                          <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Description"
                      value={limit.description}
                      onChange={(e) => updateAuthorityLimit(idx, 'description', e.target.value)}
                    />
                  </Grid>
                  {(limit.category === 'Financial' || limit.category === 'Procurement') && (
                    <>
                      <Grid item xs={6} md={2}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Currency</InputLabel>
                          <Select
                            value={limit.currency || 'USD'}
                            onChange={(e) => updateAuthorityLimit(idx, 'currency', e.target.value)}
                            label="Currency"
                          >
                            {CURRENCIES.map((cur) => (
                              <MenuItem key={cur} value={cur}>{cur}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Limit"
                          value={limit.monetaryLimit || ''}
                          onChange={(e) => updateAuthorityLimit(idx, 'monetaryLimit', parseFloat(e.target.value) || undefined)}
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} md={1}>
                    <IconButton color="error" onClick={() => removeAuthorityLimit(idx)}>
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            ))}
            <Button startIcon={<AddIcon />} onClick={addAuthorityLimit}>
              Add Authority Limit
            </Button>
          </AccordionDetails>
        </Accordion>
        
        {/* Professional Registrations */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={1}>
              <SchoolIcon color="primary" />
              <Typography variant="h6">Professional Registrations</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {(formData.professionalRegistrations || []).map((reg, idx) => (
              <Paper key={idx} sx={{ p: 2, mb: 2 }} variant="outlined">
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Professional Body"
                      value={reg.body}
                      onChange={(e) => updateRegistration(idx, 'body', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Membership Type"
                      value={reg.membershipType || ''}
                      onChange={(e) => updateRegistration(idx, 'membershipType', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={reg.isRequired}
                          onChange={(e) => updateRegistration(idx, 'isRequired', e.target.checked)}
                        />
                      }
                      label="Required"
                    />
                  </Grid>
                  <Grid item xs={12} md={1}>
                    <IconButton color="error" onClick={() => removeRegistration(idx)}>
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            ))}
            <Button startIcon={<AddIcon />} onClick={addRegistration}>
              Add Registration
            </Button>
          </AccordionDetails>
        </Accordion>
        
        {/* Other Requirements & Competencies */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={1}>
              <WorkIcon color="primary" />
              <Typography variant="h6">Other Requirements & Competencies</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Other Requirements
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Add requirement..."
                value={newRequirement}
                onChange={(e) => setNewRequirement(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addToArray('otherRequirements', newRequirement);
                    setNewRequirement('');
                  }
                }}
              />
              <Button
                variant="outlined"
                onClick={() => {
                  addToArray('otherRequirements', newRequirement);
                  setNewRequirement('');
                }}
              >
                Add
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {(formData.otherRequirements || []).map((item, idx) => (
                <Chip
                  key={idx}
                  label={item}
                  onDelete={() => removeFromArray('otherRequirements', idx)}
                />
              ))}
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Required Competencies
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Add competency..."
                value={newCompetency}
                onChange={(e) => setNewCompetency(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addToArray('requiredCompetencies', newCompetency);
                    setNewCompetency('');
                  }
                }}
              />
              <Button
                variant="outlined"
                onClick={() => {
                  addToArray('requiredCompetencies', newCompetency);
                  setNewCompetency('');
                }}
              >
                Add
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {(formData.requiredCompetencies || []).map((item, idx) => (
                <Chip
                  key={idx}
                  label={item}
                  onDelete={() => removeFromArray('requiredCompetencies', idx)}
                />
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
        
        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
          <DialogTitle>Delete Job Description?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete your job description? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button onClick={handleDelete} color="error" disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </PageWithHeader>
  );
};

export default MyJobDescriptionForm;
